import { useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { GenericOrder, PaymentResult } from '../../services/exports/Interfaces';
import { ERROR_CODES, PaymentResultErrorCode, PaymentStatus } from '../../services/exports/Constants';
import PaymentManager from '../../services/api/PaymentManager';
import paymentLog from '../../services/helpers/PaymentLogger';
import { delay } from '../../utils/loading';

export const SPREEDLY_FINGERPRINT_EL_ID = 'spreedly-3ds-fingerprint';
export const SPREEDLY_CHALLENGE_EL_ID = 'spreedly-3ds-challenge';

// '03' = a 500x600 challenge window — must match the challenge container size.
const CHALLENGE_WINDOW_SIZE = '03';
const CONFIRM_ATTEMPTS = 5;
const CONFIRM_RETRY_DELAY_MS = 2000;

interface RunArgs {
  order: GenericOrder;
  transactionToken: string;
}

/**
 * Why the confirm is happening: `completed` after a client-side success signal, `timeout` after
 * Spreedly gave up waiting on the issuer, `cancelled` after the customer backed out. The backend
 * call is identical — what differs is the severity of a still-pending answer and, for `cancelled`,
 * how long we are willing to wait for one (see CONFIRM_ATTEMPTS_BY_ORIGIN).
 */
type ConfirmOrigin = 'completed' | 'timeout' | 'cancelled';

/**
 * A cancelling customer is waiting to leave the screen, so the retry ladder is skipped: the one
 * question worth asking is whether the issuer already approved before they backed out. Still
 * pending after that single attempt stays the backend sweep's to resolve.
 */
const CONFIRM_ATTEMPTS_BY_ORIGIN: Record<ConfirmOrigin, number> = {
  completed: CONFIRM_ATTEMPTS,
  timeout: CONFIRM_ATTEMPTS,
  cancelled: 1,
};

interface ActiveRun {
  transactionToken: string;
  finish: (result: PaymentResult) => void;
  confirm: (origin: ConfirmOrigin) => Promise<PaymentResult>;
}

interface ErrorResponseBody {
  data?: { message?: string };
}

/**
 * Bound once per SDK instance rather than per hook instance: Spreedly's `on` only accumulates
 * handlers and `removeHandlers()` would also strip the card form's tokenization listeners, so a
 * per-mount ref would leave one dead handler behind on every remount. Keyed off the instance so a
 * fresh SDK (a reloaded page in the same module registry, or a test stub) rebinds.
 */
let boundSdk: Spreedly.Sdk | null = null;

/**
 * The run the single listener routes to. Module-level for the same reason the binding is: the
 * listener outlives any one mount, so the pointer it reads must too. At most one 3DS run exists at
 * a time (one pay button, one charge), and `null` means "no run" — exactly as the per-hook ref did.
 */
let activeRun: ActiveRun | null = null;

/** The mounted hook that owns `challengeActive`, so the listener can drive the overlay. */
let setChallengeActiveForActiveRun: ((active: boolean) => void) | null = null;

export function resetSpreedlyThreeDsForTests(): void {
  boundSdk = null;
  activeRun = null;
  setChallengeActiveForActiveRun = null;
}

function setChallengeActive(active: boolean): void {
  setChallengeActiveForActiveRun?.(active);
}

function bindStatusListener(spreedly: Spreedly.Sdk): void {
  if (boundSdk === spreedly) {
    return;
  }

  boundSdk = spreedly;

  spreedly.on('3ds:status', async (event: { action: string; context?: unknown }) => {
    const run = activeRun;

    if (!run) {
      return;
    }

    paymentLog.info('spreedly', 'threeds_status', {
      action: event?.action,
      payment_method: 'card',
    });

    if (event?.action === 'challenge') {
      setChallengeActive(true);

      return;
    }

    // 'succeeded' and 'trigger-completion' both mean the authentication is
    // done client-side; 'finalization-timeout' means Spreedly stopped waiting
    // — in every case the backend is the source of truth, so settle there.
    if (['succeeded', 'trigger-completion'].includes(event?.action)) {
      setChallengeActive(false);
      run.finish(await run.confirm('completed'));

      return;
    }

    if (event?.action === 'finalization-timeout') {
      setChallengeActive(false);
      run.finish(await run.confirm('timeout'));

      return;
    }

    if (event?.action === 'error') {
      setChallengeActive(false);
      paymentLog.error('spreedly', 'threeds_error', {
        payment_method: 'card',
        context: typeof event?.context === 'string' ? event.context : undefined,
      });
      run.finish({ status: PaymentStatus.Failed, error: null, result: null });
    }
  });
}

/**
 * Drives Spreedly's 3DS2 Global lifecycle for one pending charge: device
 * fingerprinting in a hidden iframe, the challenge (if any) in a visible one,
 * then a synchronous settle against the backend confirm endpoint. Every card
 * charge starts pending under 3DS2 Global — the 402 is the happy path, so this
 * runs on effectively every Spreedly payment.
 *
 * The '3ds:status' listener is registered once per SDK instance (module scope, above) and routes
 * events to the active run through a module-level pointer: Spreedly's `on` only accumulates
 * handlers, and `removeHandlers()` would also strip the card form's tokenization listeners.
 */
export default function useSpreedlyThreeDs() {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const [challengeActive, setChallengeActiveState] = useState(false);

  const environmentKey =
    company?.service_provider?.spreedly_environment_key ?? company?.spreedly_environment_key ?? null;

  function serializeBrowserInfo(): string | null {
    const spreedly = window.Spreedly;

    if (!spreedly?.ThreeDS?.serialize) {
      return null;
    }

    return spreedly.ThreeDS.serialize(CHALLENGE_WINDOW_SIZE, 'text/html');
  }

  async function confirmWithBackend(
    order: GenericOrder,
    transactionToken: string,
    origin: ConfirmOrigin,
  ): Promise<PaymentResult> {
    const failed: PaymentResult = { status: PaymentStatus.Failed, error: null, result: null };
    const attempts = CONFIRM_ATTEMPTS_BY_ORIGIN[origin];

    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      const { success, response, status } = await PaymentManager.submitSpreedlyPaymentDetails(
        {
          company_id: company?.id,
          transaction_token: transactionToken,
          order_uuid: order?.uuid,
        },
        { [ERROR_CODES.CARD_EXCEPTION]: () => null },
      );

      if (success) {
        paymentLog.info('spreedly', 'payment_details_paid', {
          order_uuid: order?.uuid,
          payment_method: 'card',
        });

        return { status: PaymentStatus.Succeeded, error: null, result: null };
      }

      // 409: the backend settled the charge and Spreedly refused it. A decline is the
      // customer's business, not an application fault — a breadcrumb, and no retry,
      // since the outcome is already final.
      if (status === 409) {
        paymentLog.info('spreedly', 'payment_details_declined', {
          order_uuid: order?.uuid,
          payment_method: 'card',
        });

        return failed;
      }

      // Anything but "still pending" is a real failure — retrying would only repeat it.
      if (status !== 402) {
        paymentLog.error('spreedly', 'payment_details_failed', {
          order_uuid: order?.uuid,
          payment_method: 'card',
          status,
          origin,
          message: (response as ErrorResponseBody)?.data?.message,
        });

        return failed;
      }

      // Still pending at Spreedly — the settle can trail the challenge by a
      // moment, so retry before giving up.
      if (attempt < attempts) {
        await delay(CONFIRM_RETRY_DELAY_MS);

        continue;
      }

      // Spreedly stopped waiting on the issuer, or the customer walked away, and the backend
      // still sees the charge pending: an expected outcome the reconcile sweep owns, not a client
      // failure — so a breadcrumb.
      if (origin === 'timeout' || origin === 'cancelled') {
        paymentLog.warn('spreedly', 'payment_details_pending', {
          order_uuid: order?.uuid,
          payment_method: 'card',
          origin,
          attempts: attempt,
        });

        return failed;
      }

      // The client saw the authentication succeed, yet Spreedly never reported the charge as
      // settled: that mismatch IS anomalous and stays a Sentry issue.
      paymentLog.issue('spreedly', 'payment_details_failed', {
        order_uuid: order?.uuid,
        payment_method: 'card',
        status,
        origin,
        attempts: attempt,
        message: (response as ErrorResponseBody)?.data?.message,
      });

      return failed;
    }

    return failed;
  }

  function run({ order, transactionToken }: RunArgs): Promise<PaymentResult> {
    const spreedly = window.Spreedly;

    if (!environmentKey || !spreedly?.ThreeDS?.Lifecycle) {
      paymentLog.issue('spreedly', 'threeds_unavailable', {
        payment_method: 'card',
        has_environment_key: !!environmentKey,
      });

      return Promise.resolve({ status: PaymentStatus.Failed, error: null, result: null });
    }

    bindStatusListener(spreedly);
    setChallengeActiveForActiveRun = setChallengeActiveState;

    return new Promise<PaymentResult>((resolve) => {
      activeRun = {
        transactionToken,
        confirm: (origin) => confirmWithBackend(order, transactionToken, origin),
        finish: (result) => {
          activeRun = null;
          resolve(result);
        },
      };

      // A previous run (cancelled or finished) leaves its iframes behind and the lifecycle only
      // appends — without this the new challenge frame stacks under the old one.
      [SPREEDLY_FINGERPRINT_EL_ID, SPREEDLY_CHALLENGE_EL_ID].forEach((id) =>
        document.getElementById(id)?.replaceChildren(),
      );

      const lifecycle = new spreedly.ThreeDS.Lifecycle({
        environmentKey,
        hiddenIframeLocation: SPREEDLY_FINGERPRINT_EL_ID,
        challengeIframeLocation: SPREEDLY_CHALLENGE_EL_ID,
        transactionToken,
        challengeIframeClasses: 'w-full h-full border-0',
      });

      lifecycle.start();
    });
  }

  /**
   * The customer backs out of the challenge (button or Escape). Spreedly has no API to abort a
   * lifecycle, and the purchase still completes if the issuer had already approved — so backing out
   * cannot simply be reported as a failure: the backend would later mark the order Paid while the
   * customer had been told the payment failed, and walked away or paid cash.
   *
   * So the backend is asked once (never the full retry ladder — a cancelling customer is waiting to
   * leave) and the run settles as Succeeded if the charge did go through. Only a still-pending or
   * refused answer becomes the cancelled failure the customer sees, and a genuinely-pending charge
   * stays the reconcile sweep's to fail.
   *
   * The activeRun is dropped up front so any later `3ds:status` from the abandoned lifecycle hits
   * the `!run` guard rather than racing this confirm to `finish`.
   */
  async function cancel() {
    const run = activeRun;

    if (!run) {
      return;
    }

    activeRun = null;
    paymentLog.info('spreedly', 'payment_cancelled', { payment_method: 'card' });
    setChallengeActiveState(false);

    const result = await run.confirm('cancelled');

    if (result.status === PaymentStatus.Succeeded) {
      // The issuer had already approved before the customer backed out. Telling them it failed
      // would strand a paid order — report the payment that actually happened.
      paymentLog.info('spreedly', 'payment_cancelled_but_paid', { payment_method: 'card' });
      run.finish(result);

      return;
    }

    run.finish({ status: PaymentStatus.Failed, error: { code: PaymentResultErrorCode.Cancelled }, result: null });
  }

  return { run, cancel, serializeBrowserInfo, challengeActive };
}
